import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const MiniCalendar = () => {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const hasEvent = (checkDate: Date) => {
    return events.some(event => 
      event.date.getDate() === checkDate.getDate() &&
      event.date.getMonth() === checkDate.getMonth() &&
      event.date.getFullYear() === checkDate.getFullYear()
    );
  };

  const getEventType = (checkDate: Date) => {
    const event = events.find(event => 
      event.date.getDate() === checkDate.getDate() &&
      event.date.getMonth() === checkDate.getMonth() &&
      event.date.getFullYear() === checkDate.getFullYear()
    );
    return event?.type || "normal";
  };

  // Buscar eventos reais do banco de dados
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Buscar compromissos/agenda
        const { data: appointments } = await supabase
          .from('appointments')
          .select('id, title, appointment_date, status')
          .eq('user_id', user.id)
          .gte('appointment_date', new Date().toISOString())
          .order('appointment_date', { ascending: true })
          .limit(10);

        // Buscar prazos/tarefas
        const { data: tasks } = await supabase
          .from('tasks')
          .select('id, title, due_date, priority')
          .eq('user_id', user.id)
          .gte('due_date', new Date().toISOString())
          .order('due_date', { ascending: true })
          .limit(10);

        // Combinar e formatar eventos
        const allEvents = [
           ...(appointments?.map(apt => ({
             date: new Date(apt.appointment_date),
             title: apt.title,
             type: apt.status === 'Confirmado' ? 'urgent' : 'normal'
           })) || []),
          ...(tasks?.map(task => ({
            date: new Date(task.due_date),
            title: task.title,
            type: task.priority === 'Alta' ? 'urgent' : task.priority === 'Média' ? 'warning' : 'normal'
          })) || [])
        ];

        setEvents(allEvents);
      } catch (error) {
        console.error('Erro ao buscar eventos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <Card 
      className="bg-white shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group overflow-hidden relative cursor-pointer"
      onClick={() => navigate('/agenda')}
    >
      <CardHeader className="relative bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-black font-bold">
          <div className="p-2 bg-gradient-to-br from-[#c4ab83] to-[#b49a73] rounded-lg shadow-md">
            <CalendarIcon className="w-4 h-4 text-white" />
          </div>
          Mini Calendário
          <Badge variant="outline" className="ml-auto bg-[#c4ab83]/20 border-[#c4ab83] text-[#c4ab83] font-medium">
            {new Date().toLocaleDateString('pt-BR', { month: 'short' })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            className="rounded-md border-0"
            classNames={{
              months: "flex w-full flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 flex-1",
              month: "space-y-4 w-full flex flex-col",
              table: "w-full h-full border-collapse space-y-1",
              head_row: "flex w-full",
              head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] flex-1 text-center",
              row: "flex w-full mt-2",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 flex-1",
              day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md mx-auto flex items-center justify-center text-sm relative",
              day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
              day_today: "bg-accent text-accent-foreground font-semibold",
              day_outside: "text-muted-foreground opacity-50",
              day_disabled: "text-muted-foreground opacity-50",
              day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
            }}
            components={{
              DayContent: ({ date: dayDate }) => (
                <div className="relative w-full h-full flex items-center justify-center">
                  <span className="text-sm">{dayDate.getDate()}</span>
                  {hasEvent(dayDate) && (
                    <div className={`absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${
                      getEventType(dayDate) === 'urgent' ? 'bg-destructive' :
                      getEventType(dayDate) === 'warning' ? 'bg-warning' : 'bg-tertiary'
                    }`} />
                  )}
                </div>
              )
            }}
          />
        </div>
        
        {/* Próximos eventos */}
        <div className="border-t border-border/50 p-4 space-y-2">
          <h4 className="text-sm font-medium text-foreground mb-3">Próximos Eventos</h4>
          {loading ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : events.length > 0 ? (
            events.slice(0, 3).map((event, index) => (
              <div key={index} className="flex items-center gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${
                  event.type === 'urgent' ? 'bg-destructive animate-pulse' :
                  event.type === 'warning' ? 'bg-warning' : 'bg-tertiary'
                }`} />
                <span className="text-muted-foreground">
                  {event.date.getDate()}/{event.date.getMonth() + 1}
                </span>
                <span className="flex-1 text-foreground">{event.title}</span>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground text-center py-2">
              Nenhum evento próximo
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MiniCalendar;